import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const utilities = ['Electricity', 'Gas', 'Water'];

export function useDashboardData(
    clientId: string,
    getDashboardFn: (id: string) => Promise<any>
) {
    const [chartData, setChartData] = useState<any>({});
    const [costData, setCostData] = useState<any>({});

    const [duration, setDuration] = useState('');
    const [totalProperties, setTotalProperties] = useState('0');
    const [totalMeters, setTotalMeters] = useState('0');

    // 🔥 NEW STATES
    const [totalConsumption, setTotalConsumption] = useState<any>({});
    const [comparisons, setComparisons] = useState<any>({});
    const [peakUsage, setPeakUsage] = useState<any>({});
    const [averages, setAverages] = useState<any>({});
    const [insights, setInsights] = useState<any>({});
    const [totalCostAllUtilities, setTotalCostAllUtilities] = useState(0);

    const [loading, setLoading] = useState(true);
    const [yearlyTrends, setYearlyTrends] = useState<any>({});

    const formatChartData = (yearlyData: any[], utility: string, isCost = false) => {
        if (!yearlyData.length) return null;

        const allMonths = Array.from(
            new Set(yearlyData.flatMap((entry: { label: string[] }) => entry.label))
        ).sort(
            (a, b) =>
                new Date(`01 ${a} 2000`).getTime() -
                new Date(`01 ${b} 2000`).getTime()
        );

        return {
            categories: allMonths,
            series: yearlyData.map((yearEntry: any) => {
                const dataMap = Object.fromEntries(
                    yearEntry.label.map((month: string, i: number) => [
                        month,
                        yearEntry.data[i],
                    ])
                );

                return {
                    name: yearEntry.year,
                    data: allMonths.map((month) => dataMap[month] || 0),
                };
            }),
            si_unit: isCost
                ? ''
                : utility === 'Electricity'
                    ? 'kWh'
                    : utility === 'Water'
                        ? 'CuM2'
                        : 'kWh',
        };
    };

    // =========================
    // 📡 FETCH DATA
    // =========================
    const fetchDashboardData = useCallback(async (id: string) => {
        try {
            setLoading(true);

            const data = await getDashboardFn(id);

            setDuration(data.duration);
            setTotalProperties(data.total_properties);
            setTotalMeters(data.total_meters);

            // 🔥 NEW DATA
            setTotalConsumption(data.total_consumption || {});
            setComparisons(data.comparisons || {});
            setPeakUsage(data.peak_usage || {});
            setAverages(data.averages || {});
            setInsights(data.insights || {});
            setTotalCostAllUtilities(data.total_cost_all_utilities || 0);

            // =========================
            // 📊 READINGS
            // =========================
            const readingsData: any = {};
            utilities.forEach((utility) => {
                readingsData[utility] = formatChartData(
                    data.readings?.[utility] || [],
                    utility
                );
            });

            readingsData['Electricity-Gas'] = combineElectricityGas(
                readingsData['Electricity'],
                readingsData['Gas']
            );

            const costsFormatted: any = {};
            utilities.forEach((utility) => {
                costsFormatted[utility] = formatChartData(
                    data.costs?.[utility] || [],
                    utility,
                    true
                );
            });

            const yearlyTrends: any = {};

            utilities.forEach((utility) => {
                const yearlyData = data.readings?.[utility] || [];

                readingsData[utility] = formatChartData(yearlyData, utility);

                yearlyTrends[utility] = computeYearlyTotals(yearlyData);
            });

            setYearlyTrends(yearlyTrends);

            setChartData(readingsData);
            setCostData(costsFormatted);

        } catch (error: any) {
            toast.error(error.message || 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    }, [getDashboardFn]);

    const combineElectricityGas = (electricity: any, gas: any) => {
        if (!electricity || !gas) return null;

        const categories = electricity.categories;

        const combinedSeries = electricity.series.map((yearSeries: any, index: number) => {
            const gasSeries = gas.series[index];

            return {
                name: yearSeries.name,
                data: yearSeries.data.map((val: number, i: number) => {
                    return val + (gasSeries?.data[i] || 0);
                })
            };
        });

        return {
            categories,
            series: combinedSeries,
            si_unit: 'kWh'
        };
    };

    const computeYearlyTotals = (yearlyData: any[]) => {
        if (!yearlyData.length) return null;

        const totals = yearlyData.map((yearEntry) => ({
            year: yearEntry.year,
            total: yearEntry.data.reduce((sum: number, val: number) => sum + val, 0)
        }));

        if (totals.length < 2) return null;

        const current = totals[totals.length - 1];
        const previous = totals[totals.length - 2];

        const percentage =
            previous.total > 0
                ? ((current.total - previous.total) / previous.total) * 100
                : 0;

        return {
            currentYear: current.year,
            previousYear: previous.year,
            percentage: Number(percentage.toFixed(2))
        };
    };

    useEffect(() => {
        if (!clientId) return;
        fetchDashboardData(clientId);
    }, [clientId, fetchDashboardData]);

    // =========================
    // 🎯 RETURN CLEAN DATA
    // =========================
    return {
        // charts
        electricityData: chartData.Electricity,
        gasData: chartData.Gas,
        waterData: chartData.Water,
        electricityGasData: chartData['Electricity-Gas'],

        electricityCostData: costData.Electricity,
        gasCostData: costData.Gas,
        waterCostData: costData.Water,

        // stats
        totalProperties,
        totalMeters,
        duration,
        loading,

        // 🔥 NEW POWER DATA
        totalConsumption,
        comparisons,
        peakUsage,
        averages,
        insights,
        totalCostAllUtilities,
        yearlyTrends
    };
}